import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EvaluationAthleteProvider } from '../../providers/evaluation-athlete-provider.services';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EvaluationAthleteQuestionProvider } from '../../providers/evaluation-athlete-question-provider.services';
import { EvaluationAthleteAnswerProvider } from '../../providers/evaluation-athlete-answer-provider.services';
import { CoachesProvider } from '../../../coaches/providers/coaches-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { Title } from '@angular/platform-browser';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { forkJoin, Observable, of, Subscription, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { EvaluationAthlete } from "../../../common/models/evaluation-athlete";
import { Question } from "../../../common/models/question";
import { Answer } from "../../../common/models/answer";
import { Coach } from "../../../common/models/coach";
import { ConfigProvider } from "../../../config-provider.service";
import { HasPendingChangesGuard } from "../../../common/guards/has-pending-changes";
import { CurrentUser } from "../../../common/providers/current-user";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { ArrayUtils } from "../../../common/providers/array-utils";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { OrganizationRole } from "../../../common/models/organization-role";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { MatCardContent, MatCardHeader, MatCardModule, MatCardSubtitle } from "@angular/material/card";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormField, MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { EvaluationAthleteSideMenuComponent } from "../../components/side-menu/side-menu";
import { NgClass, NgIf } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import {
  EvaluationAthleteQuestionGroupComponent
} from "../../components/question-group/evaluation-athlete-question-group";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { MatInput, MatInputModule } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

@Component({
  selector: 'app-evaluations-athlete-edit',
  templateUrl: './evaluations-athlete-edit.component.html',
  styleUrls: ['./evaluations-athlete-edit.scss'],
  imports: [
    ModuleContentComponent,
    Error404Component,
    Error401Component,
    HeaderComponent,
    SideMenuComponent,
    MainActionComponent,
    PageContentComponent,
    MatCardModule,
    MatLabel,
    MatFormFieldModule,
    EvaluationAthleteSideMenuComponent,
    NgIf,
    NgClass,
    TranslateModule,
    AppDatePipe,
    ReactiveFormsModule,
    FlexLayoutModule,
    EvaluationAthleteQuestionGroupComponent,
    CdkTextareaAutosize,
    MatInputModule,
    MatButton,
    MatChipsModule,
  ],
  standalone: true
})
export class EvaluationsAthleteEditComponent implements OnInit, OnDestroy {

    public readonly NOTES_MAX_LENGTH: number = 5000;

    public pageTitle = 'EvaluationAthleteList';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;
    public evaluationAthlete: EvaluationAthlete = {} as EvaluationAthlete
    public evaluationsAthletes: EvaluationAthlete[] = [];
    public questions: Question[] = [];
    public answers: Answer[] = [];
    public currentEvaluationType = '';

    public isSubmitting = false;
    public evaluationForm: FormGroup;
    // public evaluationAthleteNotes: FormControl = new FormControl('');

    public questionsGrouped: any[] = [];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | null = null;

    @HostListener('window:beforeunload')
    doSomething() {
        return !this._changesGuard.hasChanges();
    }

    private coach: Coach = {} as Coach;
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private title: Title,
        public _changesGuard: HasPendingChangesGuard,
        public breadcrumbProvider: BreadcrumbProvider,
        private currentUser: CurrentUser,
        private currentUserRoles: CurrentUserRolesProvider,
        private datePipe: AppDatePipe,
        private route: ActivatedRoute,
        private evaluationAthleteProvider: EvaluationAthleteProvider,
        private arrayUtils: ArrayUtils  ,
        private fb: FormBuilder,
        private utils: UtilsProvider,
        private toaster: MatSnackBar,
        private translator: TranslateService,
        private router: Router,
        private questionProvider: EvaluationAthleteQuestionProvider,
        private answerProvider: EvaluationAthleteAnswerProvider,
        private coachProvider: CoachesProvider,
        private dialog: MatDialog,
    ) {
        this.evaluationForm = this.fb.group({
            id: new FormControl(null),
            athleteUserId: new FormControl(null),
            coachUserId: new FormControl(null),
            evaluationAthleteId: new FormControl(null),
            questionsAthlete: this.fb.array([]),
            questionsCoach: this.fb.array([]),
            notes: new FormControl(null),
        });
    }

    public ngOnInit() {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { // { organization: Organization; athlete: Athlete; evaluationAthlete: EvaluationAthlete }
                if (data.organization) {
                    this.organization = data.organization;
                }

                if (data.athlete) {
                    this.athleteUser = data.athlete;
                }
                if (data.evaluationAthlete) {
                    this.evaluationAthlete = data.evaluationAthlete;
                    this.currentEvaluationType = data.evaluationAthlete.evaluation.type;
                    this.answers = data.evaluationAthlete.answers;
                }
                this.loadData();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public loadData() {
      // TODO: check rights
      if (this.evaluationAthlete && !true) {
          this.is401 = true;
          return;
      }

      this.evaluationAthleteProvider.getAll(this.organization, this.athleteUser.athlete!).pipe(
        switchMap((evaluationsAthletes) => {
          this.evaluationsAthletes = evaluationsAthletes;
          return forkJoin([
            this.questionProvider.findByEvaluationAthleteId(this.organization.id!, this.evaluationAthlete.id!),
            this.coachProvider.get(this.organization.id!, this.currentUser.user!.id!),
          ]).pipe(
            map(([questions, coachUser]) => {
              this.questionsGrouped = this.arrayUtils.groupByAttribute(questions, 'category', 'name');

              this.evaluationForm.get('coachUserId')?.setValue(coachUser.id);
              this.evaluationForm.get('id')?.setValue(this.evaluationAthlete.id);
              this.evaluationForm.get('athleteUserId')?.setValue(this.athleteUser.id);
              this.evaluationForm.get('notes')?.setValue(this.evaluationAthlete.notes);

              this.evaluationForm.get('evaluationAthleteId')?.setValue(this.evaluationAthlete.id);
            }),
          );
        }),
      ).subscribe({
        next: () => {
          this.createForm();
          this.createPageTitle();
          this.createBreadcrumb();
          this.isLoading = false;
        },
      });
    }

    public submitForm(): Observable<EvaluationAthlete> {
      return this.evaluationAthleteProvider.update(this.organization, this.evaluationForm.value).pipe(
        catchError((error) => {
          console.error('Error saving evaluation:', error);
          this.isSubmitting = false;
          this.isLoading = false;
          return of({} as EvaluationAthlete);
        }),
      );
    }

    public onFormChanged(): void {
        this._changesGuard.registerChanges(() => this.onSaveClicked());
    }

    public onSaveClicked(): any {
      if (this.evaluationForm.valid) {
          this.isSubmitting = true;
          // this.isLoading = true;
          this.submitForm().subscribe({
            next: () => {
              this.toaster.open(this.translator.instant('Evaluation_Saved'), '', { duration: 5000 });
              this.isSubmitting = false;
              this.isLoading = false;
              this._changesGuard.unregisterChanges();
              return true;
            },
            error: (error) => {
              this.isSubmitting = false;
              this.isLoading = false;
              return false;
            },
          });
      } else {
          this.utils.validateAllFormFields(this.evaluationForm);
          this.isSubmitting = false;
          this.isLoading = false;
          return false;
      }
    }

    public closeEvaluation() {
      // this.authorizationChecker.denyAccessUnlessGranted(this.ROLE_COACH());

      const dialog: MatDialogRef<any> = this.dialog.open(ConfirmDialogComponent, {
        data: {
          confirmText: this.translator.instant('Confirm_Close_Evaluation'),
          confirmActionText: this.translator.instant('Close'),
          color: 'accent'
        }
      });

      this.subscriptions.push(
        dialog.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.isSubmitting = true;

            if (this.evaluationForm.valid) {
              let updateObservable: Observable<any> = of(null);

              if (this._changesGuard.hasChanges()) {
                updateObservable = this.evaluationAthleteProvider.update(this.organization, this.evaluationForm.value).pipe(
                  map(() => {
                    this._changesGuard.unregisterChanges();
                  })
                );
              }

              updateObservable.pipe(
                switchMap(() => this.evaluationAthleteProvider.closeEvaluation(this.organization, this.evaluationForm.value))
              ).subscribe({
                next: () => {
                  this.toaster.open(this.translator.instant('Evaluation_Closed'), '', { duration: 5000 });
                  this.router.navigate([`${this.organization!.code}/athletes/${this.athleteUser.id}/evaluations/${this.evaluationAthlete.id}`]);
                },
                error: (error: any) => {
                  console.error('Error closing evaluation:', error);
                },
                complete: () => {
                  this.isSubmitting = false;
                }
              });
            } else {
              this.utils.validateAllFormFields(this.evaluationForm);
              this.isSubmitting = false;
            }
          }
        })
      );
    }

    // public ROLE_COACH() {
    //     return OrganizationRole.ID_COACH;
    // }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)){
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: this.athleteUser.firstName?.substr(0, 1) + '. ' + this.athleteUser.lastName,
            title: this.athleteUser.firstName + ' ' + this.athleteUser.lastName,
            url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id
        });
        crumbs.push({
            shortTitle: this.translator.instant('EvaluationOf', { date: this.datePipe.transform(this.evaluationAthlete.date, 'mediumDate') }),
            title: this.translator.instant('EvaluationOf', { date: this.datePipe.transform(this.evaluationAthlete.date, 'mediumDate') }),
            url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/evaluations/' + this.evaluationAthlete.id
        });
        crumbs.push({
            shortTitle: 'Edition',
            title: 'Edition',
            url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/evaluations/' + this.evaluationAthlete.id + '/edit'
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

    private createPageTitle(){
        this.pageTitle = this.translator.instant('EvaluationOf', { date: this.datePipe.transform(this.evaluationAthlete.date, 'mediumDate') });
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createForm() {
        for (const type of Object.keys(this.questionsGrouped)) {
            // @ts-ignore
          for (const question of this.questionsGrouped[type]) {
                this.addAthleteQuestionToForm(question);
                this.addCoachQuestionToForm(question);
            }
        }
    }

    private addAthleteQuestionToForm(question: Question) {
        const value = this.arrayUtils.findAnswerByQuestionIdAthleteId(this.answers, question.id!, this.athleteUser.athlete!.id!);

        const questions = this.evaluationForm.get('questionsAthlete') as FormArray;
        const formGroup = this.fb.group({
            id: new FormControl(question.id),
            question: new FormControl(question),
            answer: new FormControl(value),
        });

        questions.push(formGroup);
    }

    private addCoachQuestionToForm(question: Question) {

        if (this.coach) {
            const value = this.arrayUtils.findAnswerByQuestionIdCoachId(this.answers, question.id!, this.coach.id!);

            const questions = this.evaluationForm.get('questionsCoach') as FormArray;
            const formGroup = this.fb.group({
                id: new FormControl(question.id),
                question: new FormControl(question),
                answer: new FormControl(value),
            });

            questions.push(formGroup);
        }
    }
}
